import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, ArrowUpRight, ShieldCheck } from 'lucide-react';
import Logo from './Logo';
import NewsletterSignup from './NewsletterSignup';

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-[#0F172A] text-slate-300 mt-20" data-testid="footer">
      {/* Reassurance strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { label: t('footer.trust.warranty', 'Garantie 1 an incluse') },
            { label: t('footer.trust.carpass', 'Car-Pass remis') },
            { label: t('footer.trust.tech', 'Contrôle technique récent') },
            { label: t('footer.trust.takeback', 'Reprise possible') },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 justify-center text-xs md:text-sm">
              <ShieldCheck size={16} className="text-emerald-400 flex-shrink-0" />
              <span className="text-slate-200 font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <Logo variant="light" size="md" withSlogan />
            <p className="text-slate-400 text-sm leading-relaxed mt-5 max-w-xs">
              {t('footer.description', "Votre garage de confiance pour l'achat et la revente de véhicules d'occasion en Belgique.")}
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.15em] text-emerald-300 font-bold mb-5">
              {t('footer.quickLinks', 'Navigation')}
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/', label: t('nav.home') },
                { to: '/inventory', label: t('nav.inventory') },
                { to: '/sell-your-car', label: t('nav.sell') },
                { to: '/booking', label: t('nav.booking') },
                { to: '/contact', label: t('nav.contact') },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-slate-400 hover:text-white text-sm flex items-center gap-1 group transition-colors"
                    data-testid={`footer-link-${link.to.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.15em] text-emerald-300 font-bold mb-5">
              {t('footer.contact', 'Contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-slate-300 text-sm" data-testid="footer-address">
                <MapPin size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <span>{t('location.address', 'Belgique')}</span>
              </li>
              <li>
                <a
                  href="tel:+32489432161"
                  className="flex items-center gap-3 text-slate-300 hover:text-white text-sm transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone size={16} className="text-emerald-400 flex-shrink-0" />
                  +32 489 43 21 61
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@autodistrict.be"
                  className="flex items-center gap-3 text-slate-300 hover:text-white text-sm transition-colors"
                  data-testid="footer-email"
                >
                  <Mail size={16} className="text-emerald-400 flex-shrink-0" />
                  info@autodistrict.be
                </a>
              </li>
              <li className="text-slate-400 text-xs mt-2">
                {t('contact.appointmentOnly', 'Visite uniquement sur rendez-vous')}
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.15em] text-emerald-300 font-bold mb-5">
              {t('newsletter.label', 'Newsletter')}
            </h4>
            <p className="text-slate-400 text-sm mb-4 leading-relaxed">
              {t('newsletter.intro', 'Inscrivez-vous pour suivre nos nouvelles arrivées et offres exclusives.')}
            </p>
            <NewsletterSignup variant="dark" />
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-xs">
            &copy; {new Date().getFullYear()} AutoDistrict — SHLI SRL. {t('footer.rights', 'Tous droits réservés.')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
            <Link
              to="/mentions-legales"
              className="text-slate-400 hover:text-emerald-300 transition-colors"
              data-testid="footer-link-legal-notice"
            >
              {t('footer.legalNotice', 'Mentions légales')}
            </Link>
            <span aria-hidden="true" className="text-slate-700">•</span>
            <Link
              to="/politique-confidentialite"
              className="text-slate-400 hover:text-emerald-300 transition-colors"
              data-testid="footer-link-privacy-policy"
            >
              {t('footer.privacyPolicy', 'Politique de confidentialité')}
            </Link>
          </div>
          <p className="text-emerald-300 text-xs italic font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('hero.slogan', 'Driven by Quality')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
